import React, { useState, useEffect } from 'react';
import { Building2, MapPin, Phone, Mail, Globe, Users, Calendar, Award, Plus, Search, Edit, Trash2, Eye, Grid, List, ArrowUpDown, ArrowLeft, FileText } from 'lucide-react';
import Layout from '../components/Layout';
import ClubForm from '../components/ClubForm';
import LandingConfigForm from '../components/LandingConfigForm';
import { clubService } from '../services/clubService';
import { useAuth } from '../context/AuthContext';
import { getStorageUrl } from '../utils/storageUrl';

const Club = () => {
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [currentClub, setCurrentClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageViewMode, setPageViewMode] = useState('list'); // 'list' o 'detail'
  const [searchTerm, setSearchTerm] = useState('');
  const [showClubForm, setShowClubForm] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' o 'list'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'created_at', 'active'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' o 'desc'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clubToDelete, setClubToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showLandingConfig, setShowLandingConfig] = useState(false);

  useEffect(() => {
    if (user) {
      loadClubData();
    }
  }, [user]);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const data = await clubService.getClubs();
      
      // Si es super admin, mostrar lista de clubs
      if (user?.role?.name === 'super_admin') {
        // La respuesta del backend es paginada, los clubs están en data.data
        const clubsList = data.data || [];
        setClubs([...clubsList]); // Crear nueva array para forzar re-render
        setPageViewMode('list');
      } else {
        // Si no es super admin, mostrar detalle de su club
        if (data.redirect_to === 'show') {
          const clubDetail = await clubService.getClub(data.club_id);
          setCurrentClub(clubDetail);
          setPageViewMode('detail');
        }
      }
    } catch (error) {
      console.error('Error cargando datos del club:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClub = () => {
    setEditingClub(null);
    setShowClubForm(true);
  };

  const handleEditClub = (id) => {
    const club = clubs.find(c => c.id === id);
    setEditingClub(club);
    setShowClubForm(true);
  };

  const handleClubFormSuccess = async (result) => {
    // Cerrar el formulario primero
    setShowClubForm(false);
    setEditingClub(null);
    
    // Si estamos editando, actualizar el club específico inmediatamente con el resultado
    if (editingClub && result?.id) {
      setClubs(prevClubs => {
        const updated = prevClubs.map(club => {
          if (club.id === result.id) {
            return { ...club, ...result };
          }
          return club;
        });
        return updated;
      });
      
      // Si estamos en modo detalle, actualizar también el currentClub
      if (pageViewMode === 'detail' && currentClub && result.id === currentClub.id) {
        setCurrentClub({ ...currentClub, ...result });
      }
    }
    
    // Recargar todos los datos del servidor para asegurar consistencia
    // Usar un pequeño delay para que el estado se actualice primero
    setTimeout(async () => {
      await loadClubData();
    }, 100);
  };

  const handleCloseClubForm = () => {
    setShowClubForm(false);
    setEditingClub(null);
  };

  const handleDeleteClub = (id) => {
    const club = clubs.find(c => c.id === id);
    setClubToDelete(club);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setClubToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!clubToDelete) return;

    setDeleting(true);
    try {
      await clubService.deleteClub(clubToDelete.id);
      // Recargar los datos
      await loadClubData();
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error al eliminar el club:', error);
      alert(error.response?.data?.message || 'Error al eliminar el club. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewClub = async (id) => {
    try {
      const clubDetail = await clubService.getClub(id);
      setCurrentClub(clubDetail);
      setPageViewMode('detail');
    } catch (error) {
      console.error('Error al cargar el club:', error);
      alert(error.response?.data?.message || 'Error al cargar el club. Inténtalo de nuevo.');
    }
  };

  // Función para ordenar clubs
  const sortClubs = (clubs) => {
    return [...clubs].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'active':
          aValue = a.active ? 1 : 0;
          bValue = b.active ? 1 : 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Filtrar y ordenar clubs
  const filteredClubs = sortClubs(
    clubs.filter(club =>
      club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      club.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Función para cambiar ordenamiento
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <Layout title="Club" description="Información y gestión de tu club">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // Función para construir URL del logo con parámetro de caché para forzar recarga
  const getLogoUrl = (logo) => {
    if (!logo) {
      return null;
    }
    
    const url = getStorageUrl(logo);
    if (!url) return null;
    
    // Agregar timestamp para evitar caché del navegador
    return `${url}?t=${Date.now()}`;
  };

  // Vista de detalle del club (para usuarios no super admin o cuando se ve un club específico)
  if (pageViewMode === 'detail' && currentClub) {
    return (
      <Layout title="Club" description="Información y gestión de tu club">
        {/* Botón para volver a la lista (solo para super admin) */}
        {user?.role?.name === 'super_admin' && (
          <button
            onClick={() => {
              setPageViewMode('list');
              setCurrentClub(null);
            }}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Volver a la lista de clubs
          </button>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Información del Club */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  {currentClub.logo ? (
                    <img 
                      src={getLogoUrl(currentClub.logo)}
                      alt={`Logo de ${currentClub.name}`}
                      className="h-16 w-16 object-contain mr-4 rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <Building2 size={48} className={`text-blue-600 mr-4 ${currentClub.logo ? 'hidden' : ''}`} />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{currentClub.name}</h2>
                    <p className="text-gray-600">{currentClub.description || 'Club de running profesional'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleEditClub(currentClub.id)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Edit size={20} className="mr-2" />
                  Editar
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <MapPin size={20} className="text-gray-400 mr-3" />
                    <span className="text-gray-700">{currentClub.address || 'Dirección no especificada'}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={20} className="text-gray-400 mr-3" />
                    <span className="text-gray-700">{currentClub.phone || 'Teléfono no especificado'}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail size={20} className="text-gray-400 mr-3" />
                    <span className="text-gray-700">{currentClub.email || 'Email no especificado'}</span>
                  </div>
                  <div className="flex items-center">
                    <Globe size={20} className="text-gray-400 mr-3" />
                    <span className="text-gray-700">{currentClub.website || 'Website no especificado'}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">Estado del Club</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      currentClub.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {currentClub.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Descripción</h3>
                <p className="text-gray-700 leading-relaxed">
                  {currentClub.description || 'Este club de running ofrece entrenamiento personalizado, clases grupales y eventos para corredores de todos los niveles.'}
                </p>
              </div>
            </div>
          </div>

          {/* Estadísticas y Acciones Rápidas */}
          <div className="space-y-6">
            {/* Estadísticas */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas del Club</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users size={20} className="text-blue-600 mr-3" />
                    <span className="text-gray-700">Total Socios</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{currentClub.users_count || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar size={20} className="text-green-600 mr-3" />
                    <span className="text-gray-700">Eventos Activos</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{currentClub.events_count || 0}</span>
                </div>
              </div>
            </div>

            {/* Acciones Rápidas */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => handleEditClub(currentClub.id)}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Editar Información del Club
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    if (currentClub && currentClub.id) {
                      setShowLandingConfig(true);
                    } else {
                      alert('Error: No se pudo obtener la información del club');
                    }
                  }}
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm flex items-center justify-center"
                >
                  <FileText size={16} className="mr-2" />
                  Configurar Landing
                </button>
                <button className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm">
                  Gestionar Membresías
                </button>
                <button className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm">
                  Configurar Horarios
                </button>
                <button className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm">
                  Ver Reportes
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de configuración de landing */}
        <LandingConfigForm
          isOpen={showLandingConfig}
          onClose={() => setShowLandingConfig(false)}
          clubId={currentClub?.id}
          clubSlug={currentClub?.slug}
        />
      </Layout>
    );
  }

  // Vista de lista de clubs (solo para super admin)
  return (
    <Layout title="Clubs" description="Gestiona todos los clubs del sistema">
      {/* Filtros y búsqueda */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Búsqueda */}
          <div className="w-full lg:w-64 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar clubs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Controles de vista y ordenamiento */}
          <div className="flex gap-2 flex-wrap">
            {/* Toggle de vista */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'cards' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vista de tarjetas"
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vista de lista"
              >
                <List size={18} />
              </button>
            </div>
            
            {/* Ordenamiento */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleSort('name')}
                className={`px-3 py-2 rounded-md text-sm transition-colors ${
                  sortBy === 'name' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Nombre
                {sortBy === 'name' && (
                  <ArrowUpDown size={14} className="inline ml-1" />
                )}
              </button>
              <button
                onClick={() => handleSort('active')}
                className={`px-3 py-2 rounded-md text-sm transition-colors ${
                  sortBy === 'active' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Estado
                {sortBy === 'active' && (
                  <ArrowUpDown size={14} className="inline ml-1" />
                )}
              </button>
            </div>

            {/* Botón Nuevo Club */}
            <button
              onClick={handleCreateClub}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus size={20} className="mr-2" />
              Nuevo Club
            </button>
          </div>
        </div>
      </div>

      {/* Listado de clubs */}
      {viewMode === 'cards' ? (
        // Vista de tarjetas
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredClubs.map((club) => (
            <div
              key={club.id}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md ${
                !club.active ? 'opacity-60' : ''
              }`}
            >
              {/* Header del club */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">{club.name}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${
                      club.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {club.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  {club.logo ? (
                    <div className="ml-4 flex-shrink-0">
                      <img 
                        src={getLogoUrl(club.logo)}
                        alt={`Logo de ${club.name}`}
                        className="h-20 w-20 object-contain rounded"
                        onError={(e) => {
                          console.error('Error cargando logo:', club.logo, e);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                        }}
                      />
                    </div>
                  ) : (
                    <div className="ml-4 flex-shrink-0 h-20 w-20 bg-gray-100 rounded flex items-center justify-center">
                      <Building2 size={32} className="text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div className="mb-4">
                  <p className="text-gray-600 text-sm">
                    {club.description || 'Sin descripción disponible'}
                  </p>
                </div>
              </div>

              {/* Información del club */}
              <div className="p-6">
                <div className="space-y-3 mb-6">
                  {club.address && (
                    <div className="flex items-center text-sm text-gray-700">
                      <MapPin size={16} className="text-gray-400 mr-2" />
                      {club.address}
                    </div>
                  )}
                  {club.phone && (
                    <div className="flex items-center text-sm text-gray-700">
                      <Phone size={16} className="text-gray-400 mr-2" />
                      {club.phone}
                    </div>
                  )}
                  {club.email && (
                    <div className="flex items-center text-sm text-gray-700">
                      <Mail size={16} className="text-gray-400 mr-2" />
                      {club.email}
                    </div>
                  )}
                </div>

                {/* Estadísticas */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{club.users_count || 0}</div>
                    <div className="text-xs text-gray-500">Socios</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{club.events_count || 0}</div>
                    <div className="text-xs text-gray-500">Eventos</div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewClub(club.id)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center text-sm"
                  >
                    <Eye size={16} className="mr-1" />
                    Ver
                  </button>
                  <button
                    onClick={() => handleEditClub(club.id)}
                    className="flex-1 bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center text-sm"
                  >
                    <Edit size={16} className="mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeleteClub(club.id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Vista de lista
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header de la tabla */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
              <div className="col-span-4">Club</div>
              <div className="col-span-2">Contacto</div>
              <div className="col-span-2">Ubicación</div>
              <div className="col-span-2">Estadísticas</div>
              <div className="col-span-2">Estado</div>
            </div>
          </div>
          
          {/* Filas de clubs */}
          <div className="divide-y divide-gray-200">
            {filteredClubs.map((club) => (
              <div
                key={club.id}
                className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                  !club.active ? 'opacity-60' : ''
                }`}
              >
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Información del club */}
                  <div className="col-span-4">
                    <div className="flex items-center">
                      {club.logo ? (
                        <img 
                          src={getLogoUrl(club.logo)}
                          alt={`Logo de ${club.name}`}
                          className="h-10 w-10 object-contain mr-3 rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`flex items-center ${!club.logo ? '' : 'hidden'}`}>
                        <Building2 size={20} className="text-blue-600 mr-3" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{club.name}</h3>
                        <p className="text-sm text-gray-600 truncate">
                          {club.description || 'Sin descripción'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contacto */}
                  <div className="col-span-2">
                    <div className="space-y-1">
                      {club.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail size={14} className="mr-2" />
                          <span className="truncate">{club.email}</span>
                        </div>
                      )}
                      {club.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone size={14} className="mr-2" />
                          <span>{club.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Ubicación */}
                  <div className="col-span-2">
                    {club.address && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin size={14} className="mr-2" />
                        <span className="truncate">{club.address}</span>
                      </div>
                    )}
                    {club.website && (
                      <div className="flex items-center text-sm text-gray-600 mt-1">
                        <Globe size={14} className="mr-2" />
                        <a 
                          href={club.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 truncate"
                        >
                          Sitio web
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Estadísticas */}
                  <div className="col-span-2">
                    <div className="flex gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-blue-600">{club.users_count || 0}</div>
                        <div className="text-xs text-gray-500">Socios</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-green-600">{club.events_count || 0}</div>
                        <div className="text-xs text-gray-500">Eventos</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Estado y acciones */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        club.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {club.active ? 'Activo' : 'Inactivo'}
                      </span>
                      
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleViewClub(club.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEditClub(club.id)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClub(club.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mensaje si no hay clubs */}
      {filteredClubs.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Building2 size={64} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron clubs</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm 
              ? 'No hay clubs que coincidan con la búsqueda.'
              : 'Aún no hay clubs registrados en el sistema.'
            }
          </p>
          <button
            onClick={handleCreateClub}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Crear Primer Club
          </button>
        </div>
      )}

      {/* Modal del formulario de club */}
      <ClubForm
        isOpen={showClubForm}
        onClose={handleCloseClubForm}
        onSuccess={handleClubFormSuccess}
        clubData={editingClub}
        isEditing={!!editingClub}
      />

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && clubToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 size={24} className="text-red-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Confirmar Eliminación
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  ¿Estás seguro de que quieres eliminar el club <strong>{clubToDelete.name}</strong>?
                </p>
                <p className="text-sm text-red-600 mt-2 font-medium">
                  Esta acción no se puede deshacer.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseDeleteModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} className="mr-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Club;
